import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { SPACE_FOLDER_PAGE_SIZE, SpaceKind, SpaceNodeSortBy, SpaceSearchFolderView } from 'shared';

import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { Order } from '../../../types/general.types';
import {
  SelectedSpace,
  SpaceBrowserFolder,
  SpaceBrowserPagination,
  SpaceBrowserSort,
} from '../types/data-room.types';
import { useVaultFolderContents } from '../vault/hooks/use-vault-folder-contents.hook';
import { isVaultSharedFolder } from '../vault/utils/vault-shared-folder.util';
import { useSpaceFolderContents } from './use-space-folder-contents.hook';
import { FolderTreeScope, readCachedFolderLevel } from './use-space-navigation-folders.hook';
import { useSpaceSearch } from './use-space-search.hook';

type Props = {
  folderId: null | string;
  // A room seen by somebody it was shared with: the owner's tree routes refuse them, so its
  // contents come from their own room route.
  isRecipientView?: boolean;
  // The Vault of a Stakeholder: its folders are listed and saved, and nothing is added to them.
  isVaultReadOnly?: boolean;
  // A term searches the whole space instead of listing the open folder; empty lists it.
  search?: string;
  // A tree space, a search of one and the Vault take the column and its direction.
  sort: SpaceBrowserSort;
  space: SelectedSpace;
};

const NO_FOUND_FOLDERS: SpaceSearchFolderView[] = [];
const NO_NAMES: string[] = [];
const NO_LINKS = new Map<string, string>();

// Where the open folder takes no uploads of the Vault's kind: every tree space, a recipient's room.
const NO_VAULT_UPLOAD = { canUpload: false, existingNames: NO_NAMES };

// The Vault's whole level and a preview have nothing to page.
const NO_PAGINATION: SpaceBrowserPagination = {
  canShowLess: false,
  isLoadingMore: false,
  isReloading: false,
  loadMore: () => undefined,
  nextPageSize: 0,
  remainingCount: 0,
  showLess: () => undefined,
};

// A navigation level carries no stats; a contents page carries them when the room shows them.
const toBrowserFolder = ({
  fileCount = null,
  id,
  name,
  totalSize = null,
  updatedAt = null,
}: {
  fileCount?: null | number;
  id: string;
  name: string;
  totalSize?: null | number;
  updatedAt?: null | string;
}): SpaceBrowserFolder => ({ id, itemCount: fileCount, name, size: totalSize, updatedAt });

// A navigation level comes A to Z, ties by id, as the contents do; reversed, it is their Z to A.
const inNameOrder = <T>(level: T[], sortOrder: Order): T[] =>
  sortOrder === Order.DESC ? [...level].reverse() : level;

/**
 * What the browser table lists for the open folder. A workspace or a data room pages its own
 * contents, sorted by the server — a room seen by a recipient through the recipient's own route.
 * While a folder's first page loads in name order, the folders the sidebar already holds are shown,
 * so the table is not a skeleton for a known level.
 *
 * The Vault lists a whole level at once from its own routes, as its page always did: folders with
 * their counts and sizes, then files, ordered by any of its columns. It says through `vaultUpload`
 * whether the folder takes uploads — the Vault's own rule — and keeps in `vaultFileLinks` the
 * stored link each file's rename has to send back.
 *
 * With a search term the table lists the matches of the whole space instead, paged by the server
 * for every kind of space, and `foundFolders` keeps the folders above each match for opening it.
 * A recipient's room is searched through the recipient's own route; the Vault, for a Stakeholder
 * too, through the module route that narrows it to their folders.
 */
export const useSpaceBrowserContents = ({
  folderId,
  isRecipientView = false,
  isVaultReadOnly = false,
  search = '',
  sort,
  space,
}: Props) => {
  const { by: sortBy, order: sortOrder } = sort;
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const queryClient = useQueryClient();
  const { id: spaceId, kind } = space;

  // Only the Vault has no tree of its own. A recipient's room does — read through their own route.
  const treeSpace = useMemo(
    () => (kind === SpaceKind.VAULT ? null : { id: spaceId, kind }),
    [kind, spaceId],
  );
  const scope = isRecipientView ? FolderTreeScope.RECIPIENT : FolderTreeScope.OWNER;
  const searchSpace = useMemo(() => ({ id: spaceId, kind }), [kind, spaceId]);
  const results = useSpaceSearch({ scope, search, sortBy, sortOrder, space: searchSpace });
  // The listings are not shown while a term searches, so they are not fetched either; what they
  // already hold stays cached and shows again, without a skeleton, when the term is cleared.
  const isListing = !results.isSearching;
  const contents = useSpaceFolderContents({
    enabled: isListing,
    folderId,
    scope,
    sortBy,
    sortOrder,
    space: treeSpace,
  });
  const isVault = kind === SpaceKind.VAULT;
  const vault = useVaultFolderContents({
    enabled: isVault && isListing,
    folderId,
    readOnly: isVaultReadOnly,
    sort,
  });

  const folders = useMemo((): SpaceBrowserFolder[] => {
    if (!treeSpace) return vault.folders;

    if (contents.isPending) {
      // The sidebar's level is in name order, so it stands in for the table only in that order.
      if (sortBy !== SpaceNodeSortBy.NAME) return [];

      const cached = readCachedFolderLevel(queryClient, companyId, scope, {
        kind,
        parentFolderId: folderId,
        spaceId,
      });

      // No more than the first page holds, so the table does not shrink when it arrives.
      return inNameOrder(cached ?? [], sortOrder)
        .slice(0, SPACE_FOLDER_PAGE_SIZE)
        .map(toBrowserFolder);
    }

    return contents.folders.map(toBrowserFolder);
  }, [
    companyId,
    contents.folders,
    contents.isPending,
    folderId,
    kind,
    queryClient,
    scope,
    sortBy,
    sortOrder,
    spaceId,
    treeSpace,
    vault.folders,
  ]);

  const foundRows = useMemo(
    () =>
      results.folders.map((found) => ({
        ...toBrowserFolder(found),
        ...(isVault && isVaultSharedFolder(found.name) ? { isShared: true } : {}),
      })),
    [isVault, results.folders],
  );

  const isPreview = !!treeSpace && contents.isPending && folders.length > 0;

  if (results.isSearching) {
    return {
      files: results.files,
      folders: foundRows,
      foundFolders: results.folders,
      isError: results.isError,
      isPending: results.isPending,
      isSearching: true,
      pagination: results.pagination,
      retry: results.retry,
      treeSpace,
      vaultFileLinks: NO_LINKS,
      vaultUpload: NO_VAULT_UPLOAD,
    };
  }

  if (!treeSpace) {
    return {
      files: vault.files,
      folders,
      foundFolders: NO_FOUND_FOLDERS,
      isError: vault.isError,
      isPending: vault.isPending,
      isSearching: false,
      pagination: NO_PAGINATION,
      retry: vault.retry,
      treeSpace,
      vaultFileLinks: vault.fileLinks,
      vaultUpload: { canUpload: vault.canUpload, existingNames: vault.existingNames },
    };
  }

  return {
    files: contents.files,
    folders,
    foundFolders: NO_FOUND_FOLDERS,
    isError: contents.isError,
    isPending: contents.isPending && !isPreview,
    isSearching: false,
    pagination: isPreview ? NO_PAGINATION : contents.pagination,
    retry: contents.retry,
    treeSpace,
    vaultFileLinks: NO_LINKS,
    vaultUpload: NO_VAULT_UPLOAD,
  };
};
